import React, { useEffect, useState } from 'react';
import { Phone, MessageCircle, TrendingUp, Award, Users, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { academyInfo } from '../data/mockData';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCall = () => {
    window.location.href = `tel:${academyInfo.phone}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/91${academyInfo.whatsapp}?text=Hi, I want to enquire about stock market courses`, '_blank');
  };

  return (
    <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <img
          src="/images/academy/academy-signboard.jpg"
          alt="Stocks Vision Academy"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-green-50/90 to-green-100/95"></div>
      </div>
      
      {/* Animated Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 animate-pulse" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(22, 163, 74) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-green-400 rounded-full blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-500 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-green-300 rounded-full blur-3xl opacity-15 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className={`max-w-5xl mx-auto text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Premium Badge with Animation */}
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-full mb-6 shadow-lg animate-bounce-slow">
            <Award className="h-5 w-5" />
            <span className="text-sm font-bold">Bhopal's Premier Trading Academy</span>
          </div>

          {/* Main Headline with Gradient */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
            Transform Your
            <span className="block mt-2 bg-gradient-to-r from-green-600 via-green-500 to-green-600 bg-clip-text text-transparent animate-gradient">
              Financial Future
            </span>
            <span className="block text-3xl sm:text-4xl lg:text-5xl mt-3 text-gray-800">
              Through Smart Trading
            </span>
          </h1>

          {/* Exciting Sub-headline */}
          <p className="text-lg sm:text-2xl text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed font-medium">
            Master <span className="text-green-600 font-bold">Technical Analysis</span> & 
            <span className="text-green-600 font-bold"> Options Trading</span> with 
            <span className="font-bold"> Live Market Training</span>
          </p>
          
          <p className="text-base sm:text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Join 500+ successful traders — <span className="font-bold text-green-700">offline in Bhopal or live online from anywhere in India</span>
          </p>

          {/* Animated Key Benefits */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="group flex items-center gap-2 bg-white px-5 py-3 rounded-xl shadow-lg border-2 border-green-200 hover:border-green-600 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <TrendingUp className="h-5 w-5 text-green-600 group-hover:animate-bounce" />
              <span className="text-gray-800 font-semibold">Live Market Sessions</span>
            </div>
            <div className="group flex items-center gap-2 bg-white px-5 py-3 rounded-xl shadow-lg border-2 border-green-200 hover:border-green-600 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <Users className="h-5 w-5 text-green-600 group-hover:animate-bounce" />
              <span className="text-gray-800 font-semibold">Small Batch Size</span>
            </div>
            <div className="group flex items-center gap-2 bg-white px-5 py-3 rounded-xl shadow-lg border-2 border-green-200 hover:border-green-600 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <Award className="h-5 w-5 text-green-600 group-hover:animate-bounce" />
              <span className="text-gray-800 font-semibold">Post-Course Support</span>
            </div>
          </div>

          {/* Prominent CTA Buttons with Animation */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button
              onClick={handleCall}
              size="lg"
              className="group w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110 animate-pulse-slow">

              <Phone className="mr-3 h-6 w-6 group-hover:animate-bounce" />
              Call Now: {academyInfo.phone}
            </Button>
            <Button
              onClick={handleWhatsApp}
              size="lg"
              className="group w-full sm:w-auto bg-white border-4 border-green-600 text-green-700 hover:bg-green-50 px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110">

              <MessageCircle className="mr-3 h-6 w-6 group-hover:animate-bounce" />
              WhatsApp Now
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* Urgency Indicator with Pulse Animation */}
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-red-50 border-2 border-red-300 rounded-full animate-pulse">
            <div className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </div>
            <span className="text-red-700 font-bold text-sm sm:text-base">
              ⚡ Next Batch Starting Soon - Limited Seats!
            </span>
          </div>

        </div>
      </div>
    </section>);

};

export default Hero;